import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Users, Plus, X } from "lucide-react";

export default function SplitModal({ open, onClose, onSuccess }) {
  const { t, lang } = useApp();
  const [title, setTitle] = useState("");
  const [total, setTotal] = useState("");
  const [includeMe, setIncludeMe] = useState(true);
  const [input, setInput] = useState("");
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open) { setTitle(""); setTotal(""); setIncludeMe(true); setInput(""); setPeople([]); }
  }, [open]);

  const addPerson = () => {
    const v = input.trim();
    if (v && !people.includes(v)) setPeople([...people, v]);
    setInput("");
  };

  const headcount = people.length + (includeMe ? 1 : 0);
  const totalNum = parseFloat(total) || 0;
  const share = headcount > 0 ? totalNum / headcount : 0;

  const submit = async () => {
    if (!title.trim() || totalNum <= 0 || people.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post("/split", {
        title: title.trim(), total: totalNum, participants: people, include_self: includeMe,
      });
      toast.success(`${t.toastSplitSent} · ${formatSAR(res.data.share, lang)} ${t.currency}`);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="split-modal" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> {t.splitBillTitle}
          </DialogTitle>
          <DialogDescription>{t.splitDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 pt-1">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.splitBillTitle}</label>
            <Input
              data-testid="split-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.splitTitlePlaceholder}
              className="h-12 rounded-2xl mt-1.5"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.totalAmount}</label>
            <div className="relative mt-1.5">
              <Input
                data-testid="split-total-input"
                type="number"
                inputMode="decimal"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                placeholder={t.enterAmount}
                className="h-12 rounded-2xl text-lg font-bold tabular-nums"
              />
              <span className="absolute top-1/2 -translate-y-1/2 end-4 text-sm font-bold text-muted-foreground">{t.currency}</span>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.participants}</label>
            <div className="flex gap-2 mt-1.5">
              <Input
                data-testid="split-participant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPerson(); } }}
                placeholder={t.addParticipantPlaceholder}
                className="h-12 rounded-2xl flex-1"
              />
              <Button data-testid="split-add-participant" type="button" onClick={addPerson} variant="outline" className="h-12 rounded-2xl px-4">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {people.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2.5">
                {people.map((p) => (
                  <span key={p} className="flex items-center gap-1 ps-3 pe-1.5 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                    {p}
                    <button onClick={() => setPeople(people.filter((x) => x !== p))} className="w-5 h-5 grid place-items-center rounded-full hover:bg-primary/20">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between py-1">
            <span className="text-sm font-medium">{t.includeMe}</span>
            <Switch data-testid="split-include-me" checked={includeMe} onCheckedChange={setIncludeMe} />
          </div>

          {share > 0 && people.length > 0 && (
            <div className="flex items-center justify-between p-3.5 rounded-2xl bg-muted">
              <span className="text-sm text-muted-foreground">{t.perPerson} ({headcount})</span>
              <span className="font-bold tabular-nums text-primary" data-testid="split-share-preview">
                {formatSAR(share, lang)} {t.currency}
              </span>
            </div>
          )}

          <Button
            data-testid="split-submit"
            onClick={submit}
            disabled={loading || !title || !total || people.length === 0}
            className="w-full h-[52px] rounded-2xl text-base font-bold"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.sendRequests}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
